// LOCATION: admin-portal/src/pages/StaffManagement.jsx

import React, { useEffect, useState } from 'react';
import { db } from '../firebase'; 
import { collection, onSnapshot, addDoc, updateDoc, doc, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import './UserManagement.css'; // Reuse existing table styles

const StaffManagement = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Form State
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('Sales');
  const [newDate, setNewDate] = useState('');
  const [editData, setEditData] = useState(null);

  // 1. Fetch Staff Data
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'staff'), (snapshot) => {
      const fetchedStaff = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStaff(fetchedStaff);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // 2. Add New Staff
  const handleAddStaff = async () => {
    if (!newEmail || !newDate) return alert("Please fill all fields");

    try {
      // Search for user profile first
      const q = query(collection(db, "profiles"), where("email", "==", newEmail.toLowerCase().trim()));
      const querySnap = await getDocs(q);
      
      if (querySnap.empty) {
        return alert("User profile not found! They must sign up in the app first.");
      }

      const userDoc = querySnap.docs[0];
      const userData = userDoc.data();
      const staffId = "STF" + Math.floor(1000 + Math.random() * 9000);

      await addDoc(collection(db, "staff"), {
        staffId: staffId,
        uid: userDoc.id,
        name: userData.name || "Unknown",
        email: newEmail,
        phone: userData.phone || "",
        department: newRole,
        employmentDate: newDate,
        status: "Active",
        lastActive: serverTimestamp(),
        createdAt: serverTimestamp()
      });

      alert("Staff added successfully!");
      setIsAddModalOpen(false);
      setNewEmail(''); setNewDate('');
    } catch (e) {
      console.error(e);
      alert("Error adding staff: " + e.message);
    }
  };

  // 3. Update Staff
  const handleUpdateStaff = async () => {
    if (!editData || !editData.employmentDate) return alert("Date is required");
    
    try {
      await updateDoc(doc(db, "staff", editData.id), {
        department: editData.department,
        status: editData.status,
        employmentDate: editData.employmentDate
      });
      setIsEditModalOpen(false);
    } catch (e) {
      alert("Update failed: " + e.message);
    }
  };

  if (loading) return <div className="container">Loading Staff...</div>;

  return (
    <div className="container">
      <div className="header">
        <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
            <span style={{fontSize:'24px'}}>👔</span>
            <h1>Staff Management</h1>
        </div>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Dept</th>
              <th>Status</th>
              <th>Emp. Date</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {staff.map(s => (
              <tr key={s.id}>
                <td>#{s.staffId}</td>
                <td style={{fontWeight:'bold'}}>{s.name}</td>
                <td>{s.email}</td>
                <td>{s.department}</td>
                <td>
                    <span className={`badge ${s.status === 'Active' ? 'badge-green' : 'badge-red'}`}>
                        {s.status}
                    </span>
                </td>
                <td>{s.employmentDate}</td>
                <td>
                  <button className="action-btn" onClick={() => { setEditData(s); setIsEditModalOpen(true); }}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* FAB to Add Staff */}
      <button className="fab" onClick={() => setIsAddModalOpen(true)} title="Add Staff">+</button>

      {/* --- ADD MODAL --- */}
      {isAddModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Add New Staff</h2>
            <label className="modal-label">User Email (Must exist in App)</label>
            <input className="modal-select" type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="user@example.com" />
            
            <label className="modal-label">Department</label>
            <select className="modal-select" value={newRole} onChange={e => setNewRole(e.target.value)}>
                <option>Sales</option><option>Operations</option><option>HR</option><option>Finance</option><option>Executive</option>
            </select>

            <label className="modal-label">Employment Date</label>
            <input className="modal-select" type="date" value={newDate} onChange={e => setNewDate(e.target.value)} />

            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
              <button className="save-btn" onClick={handleAddStaff}>Search & Add</button>
            </div>
          </div>
        </div>
      )}

      {/* --- EDIT MODAL --- */}
      {isEditModalOpen && editData && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Edit Staff: {editData.name}</h2>
            
            <label className="modal-label">Department</label>
            <select className="modal-select" value={editData.department} 
                onChange={e => setEditData({...editData, department: e.target.value})}>
                <option>Sales</option><option>Operations</option><option>HR</option><option>Finance</option><option>Executive</option>
            </select>

            <label className="modal-label">Status</label>
            <select className="modal-select" value={editData.status} 
                onChange={e => setEditData({...editData, status: e.target.value})}>
                <option>Active</option><option>Leave</option><option>Exit</option>
            </select>

            <label className="modal-label">Employment Date</label>
            <input className="modal-select" type="date" value={editData.employmentDate} 
                onChange={e => setEditData({...editData, employmentDate: e.target.value})} />

            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setIsEditModalOpen(false)}>Cancel</button>
              <button className="save-btn" onClick={handleUpdateStaff}>Update Staff</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffManagement; // ✅ THIS IS THE MOST IMPORTANT LINE